import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["billId"];
import { api } from 'src/api';
import { types } from 'mobx-state-tree';
import { createAsync, createSilentAsync } from 'src/utils/asyncModel';
import { uploadBillFile } from 'src/stores/billAutomation/uploadBillFile';

/**
 * Uploads a bill file and registers it: apply billing sending one file straight to the bill
 * bucket, for one property, cycle and utility (LB-5898).
 *
 * Silent, because the screen says what went wrong in its own words. The model raising a toast of
 * its own as well would mean two messages for one failed upload.
 */
const uploadBillAsync = createSilentAsync(body => {
  return async function uploadBill(options) {
    return uploadBillFile(body, options);
  };
});
const startBillAsync = createAsync(body => {
  return async function startBill(options) {
    const resp = await api.BillAutomationBills.startBill(body, options);
    return resp;
  };
});
const getBillAsync = createAsync(billId => {
  return async function getBill(options) {
    const resp = await api.BillAutomationBills.getBill(billId, options);
    return resp;
  };
});

/**
 * The same read, for the allocation table fetching the bill an allocation was made from.
 *
 * Its own model rather than a second caller of the one above, for two reasons. It is silent: a
 * bill deleted since it was allocated answers 404, and that is an allocation with no document
 * rather than something to interrupt the person about. And `run` cancels whatever its model already
 * has in flight, so sharing one with the details poll would have the two cancelling each other.
 */
const getAllocatedBillAsync = createSilentAsync(billId => {
  return async function getAllocatedBill(options) {
    const resp = await api.BillAutomationBills.getBill(billId, options);
    return resp;
  };
});

/**
 * The same read again, for apply billing asking whether the bill an allocation was made from is still on file before
 * anything else is attached to it (LB-5952). Its own model, so it neither cancels the lookup above nor is cancelled by
 * it. Silent: a 404 is the answer "no longer on file", read off the model's error.
 */
const getNamedBillOnFileAsync = createSilentAsync(billId => {
  return async function getNamedBillOnFile(options) {
    const resp = await api.BillAutomationBills.getBill(billId, options);
    return resp;
  };
});

/**
 * Recovers the bill uploaded for a billing cycle, property and utility — how apply billing finds the
 * document it was working on again after its modal closed. Resolves with a null `response` when
 * nothing has been uploaded for that cycle yet.
 */
const getBillForCycleAsync = createAsync(body => {
  return async function getBillForCycle(options) {
    const resp = await api.BillAutomationBills.getBillForCycle(body, options);
    return resp;
  };
});

/**
 * Pushes one uploaded bill to AppFolio AP. Resolves with the attempts the service created.
 * `overrideTotalAmount` is present only when a person edited the Bill total by hand, and
 * `invoiceNumber` only when they supplied one the extraction could not.
 */
const pushBillToAppfolioAsync = createAsync(body => {
  return async function pushBillToAppfolio(options) {
    const billId = body.billId,
      supplied = _objectWithoutProperties(body, _excluded);
    const resp = await api.BillAutomationBills.pushBillToAppfolio(billId, supplied, options);
    return resp;
  };
});

/** AppFolio AP push status for a batch of bills, so the dashboard can refresh several rows at once. */
const getAppfolioPushStatusAsync = createAsync(body => {
  return async function getAppfolioPushStatus(options) {
    const resp = await api.BillAutomationBills.getAppfolioPushStatus(body, options);
    return resp;
  };
});
const BillAutomationBillsApiStore = types.model('BillAutomationBillsApiStore', {
  startBillAsync,
  uploadBillAsync,
  getBillAsync,
  getAllocatedBillAsync,
  getNamedBillOnFileAsync,
  getBillForCycleAsync,
  pushBillToAppfolioAsync,
  getAppfolioPushStatusAsync
});
export const billAutomationBillsApi = types.optional(BillAutomationBillsApiStore, {});